<template>
  <!-- eslint-disable max-len -->
  <div class="root">
    <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
      <div v-if="!isLoadingComplete">
        <blade-spinner>
          <div
            v-if="loadMessage && loadMessage.length > 0"
            slot="spinner-neighbor"
            style="margin-top: 10px; font-size: 24px;"
          >{{ loadMessage }}</div>
        </blade-spinner>
      </div>
      <div v-else class="game-hub-container" :style="contentContainerStyle">
        <template v-if="hasNoContent">
          <div class="hub-header">
            <h2 id="hub-header">{{ headerText }}</h2>
            <b-checkbox
              v-if="canSelectGame"
              v-model="viewAll"
              @input="onChangeViewAll"
            >View All</b-checkbox>
          </div>
          <div class="hub-header">
            <div class>You don't have access to any content right now.  If you believe this is in error, please contact support.</div>
          </div>
        </template>
        <template v-else>
          <div class="hub-header">
            <h2>{{ headerText }}</h2>
            <b-checkbox
              v-if="canSelectGame"
              v-model="viewAll"
              @input="onChangeViewAll"
            >View All</b-checkbox>
          </div>
          <template v-if="!isProfessor && !isManager && classList.length > 0">
            <class-list
              :header="classesHeaderText"
              :list="classList"
              @play-clicked="onClassSelect"
              @reset-clicked="onClassReset"
              @details-clicked="(e) => { setTargetEntry(e); targetIsClass = true; $refs.detailsModal.show(); }"
            />
          </template>
          <template v-if="gameListContent.length > 0">
            <game-list
              :header="gamesHeaderText"
              :list="gameListContent"
              @play-clicked="onGameSelect"
              @reset-clicked="onGameReset"
              @details-clicked="(e) => { setTargetEntry(e); targetIsClass = false; $refs.detailsModal.show(); }"
            />
          </template>
          <!-- <template v-if="availableGames.length > 0">
            <game-list
              header="Available Games"
              :list="availableGames"
              @play-clicked="onClickPlay"
              @reset-clicked="onClickReset"
              @details-clicked="(e) => { targetEntry = e; $refs.detailsModal.show(); }"
            />
            <br />
          </template>
          <template v-if="unavailableGames.length > 0">
            <game-list
              header="Unavailable Games"
              :list="unavailableGames"
              @reset-clicked="onClickReset"
              @details-clicked="(e) => { targetEntry = e; $refs.detailsModal.show(); }"
            />
            <br />
          </template>-->
        </template>
      </div>
    </transition>
    <b-modal
      v-show="targetEntry !== null"
      ref="detailsModal"
      size="lg"
      centered
      hide-footer
      content-class="details-modal-base"
      header-class="details-modal-header-container"
      body-class="p-0"
    >
      <template slot="modal-header" slot-scope="{ close }">
        <div
          class="details-modal-header"
        >
          <h5
            class="modal-title"
          >
            <div v-if="targetEntry != null"
              v-html="targetEntry.thumbnailTitle || targetEntry.courseName || targetEntry.className || targetEntry.game.thumbnailTitle || targetEntry.game.title"
              style="text-align: center;"
            >
            </div>
          </h5>
          <button
            size="sm"
            variant="outline-danger"
            @click="close()"
            type="button"
            aria-label="Close"
            class="close details-modal-close-button"
          >×</button>
        </div>
      </template>
      <div v-if="targetEntry != null"
        class="details-modal-body"
      >
        <div v-if="targetEntry.game.detailsImage || targetEntry.game.titleImage"
          class="details-modal-image"
          :style="`background-image: url('${
            resolveAssetPath(targetEntry.game.detailsImage || targetEntry.game.titleImage, 'ui/customer')
          }');`"
        ></div>
        <div class="details-modal-content">
          <div v-if="targetEntry.game.detailsLogo"
            class="details-modal-logo"
            :style="`background-image: url('${
              resolveAssetPath(targetEntry.game.detailsLogo, 'ui/customer')
            }');`"
          ></div>
          <div class="details-modal-description">
            <div
              v-html="(targetEntry.game.description && targetEntry.game.description.length > 0)
                ? targetEntry.game.description
                : 'No details available.'"
            ></div>
          </div>
          <div class="details-modal-button-container">
            <button
              v-if="targetEntry.complete && !targetEntry.inProgress"
              class="btn"
              disabled
            >
              Completed
            </button>
            <button
              v-else-if="!targetEntry.disabled"
              class="btn"
              @click.prevent="targetIsClass ? onClassSelect(targetEntry) : onGameSelect(targetEntry)"
            >{{ targetEntry.childGame ? resumeCompletedText() : (targetEntry.inProgress ? (targetEntry.complete ? resumeCompletedText() : 'Resume') : 'Start') }}</button>
            <button
              v-if="!targetEntry.disabled && canRestartGame && (targetEntry.inProgress || targetEntry.complete) && !(targetEntry.multiplayer)"
              class="btn-choice"
              @click.prevent="targetIsClass ? onClassReset(targetEntry) : onGameReset(targetEntry)"
            >Restart</button>
          </div>
        </div>
      </div>
    </b-modal>
    <div v-if="shouldDisplayFooter" class="hub-footer">
      <!-- TODO: Probably just remove this -->
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ClassList from './components/GameHubClassList';
import GameList from './components/GameHubGameList';

import startSound from '../../../assets/ui/Homepage_Start.wav';
import restartSound from '../../../assets/ui/Homepage_Restart.wav';

export default {
  components: {
    ClassList,
    GameList,
  },
  data() {
    return {
      targetIsClass: false,
      targetEntry: null,
      loadMessage: 'Loading gamulation hub...',
      isLoadingComplete: false,
      viewAll: false,
      classList: [],
      gameList: [],
    };
  },
  computed: {
    ...mapGetters([
      'isLti',
      'me',
      'group',
      'isProfessor',
      'classTypeNamePlural',
      'canSelectGame',
      'canRestartGame',
      'activeEnvironment',
      'isManager',
    ]),
    registrationLinkUsed() {
      if (!this.me || !this.me.registrationLinkUsed) return null;
      return this.me.registrationLinkUsed;
    },
    classesHeaderText() {
      if (this.registrationLinkUsed && this.registrationLinkUsed.hubClassesOverrideText) {
        return this.registrationLinkUsed.hubClassesOverrideText;
      }
      return this.classTypeNamePlural;
    },
    gamesHeaderText() {
      if (this.registrationLinkUsed && this.registrationLinkUsed.hubGamesOverrideText) {
        return this.registrationLinkUsed.hubGamesOverrideText;
      }
      return 'Gamulations';
    },
    headerText() {
      // return `Gamulations and ${this.classTypeNamePlural}`;
      return 'Home Page';
    },
    contentContainerStyle() {
      return {
        height: this.shouldDisplayFooter ? 'calc(100% - 50px)' : '100%',
      };
    },
    shouldDisplayFooter() {
      return false;
    },
    hasAnyContent() {
      return this.gameList.length > 0 || this.classList.length > 0;
    },
    activeGames() {
      return this.gameList.filter(a => a.inProgress);
      // .sort(((a, b) => a.game.title.localeCompare(b.game.title, undefined, { numeric: true })));
    },
    inactiveGames() {
      return this.gameList.filter(a => !a.inProgress);
      // .sort(((a, b) => a.game.title.localeCompare(b.game.title, undefined, { numeric: true })));
    },
    availableGames() {
      return this.inactiveGames.filter(a => !a.disabled);
    },
    unavailableGames() {
      return this.inactiveGames.filter(a => a.disabled);
    },
    gameListContent() {
      // The filter just copies so we don't sort the underlying array
      return this.gameList.filter(() => true);
      // return this.activeGames.concat(this.availableGames).concat(this.unavailableGames);
    },
    classListContent() {
      // The filter just copies so we don't sort the underlying array
      return this.classList.filter(() => true);
      // .sort(((a, b) => {
      //   const aComp = a.courseName || a.className || a.game.title;
      //   const bComp = b.courseName || b.className || b.game.title;
      //   return aComp.localeCompare(bComp, undefined, { numeric: true });
      // }));
    },
    shouldDisplayGameList() {
      return true;
    },
    shouldDisplayClassList() {
      return !this.isProfessor;
    },
    hasNoContent() {
      let contentCount = 0;
      if (this.shouldDisplayGameList) {
        contentCount += this.gameListContent.length;
      }
      if (this.shouldDisplayClassList) {
        contentCount += this.classListContent.length;
      }
      return contentCount === 0;
    },
  },
  methods: {
    ...mapActions([
      'getShouldDefaultToHub',
      'getMyClasses',
      'getActiveGameSetGames',
      'flashDanger',
      'getMyGroup',
      'getMyGroupIdOnly',
      'archiveMyGroup',
      'getActiveEnvironment',
      'getActiveSignInConfig',
    ]),
    resumeCompletedText() {
      return this.targetEntry.game.thumbnailResumeCompletedTextOverride || 'Resume (Completed)';
    },
    setTargetEntry(entry) {
      this.targetEntry = entry;
      if (entry) {
        // check whether game values should override environment values
        const override = (entry.game && entry.game.thumbnailBrandingOverride);

        // set modal background color
        const gameBackgroundDefined = (entry.game && entry.game.detailsModalBackgroundColor);
        const environmentBackgroundDefined =
          (this.activeEnvironment && this.activeEnvironment.hubDetailsModalBackgroundColor);
        if (override && gameBackgroundDefined) {
          document.documentElement.style.setProperty(
            '--modalBackgroundColor', entry.game.detailsModalBackgroundColor
          );
        } else if (environmentBackgroundDefined) {
          document.documentElement.style.setProperty(
            '--modalBackgroundColor', this.activeEnvironment.hubDetailsModalBackgroundColor
          );
        } else { // fallback values
          document.documentElement.style.setProperty(
            '--modalBackgroundColor', entry.game.detailsModalBackgroundColor || 'rgba(17, 17, 17, 0.5)'
          );
        }

        // set modal text color
        const gameTextDefined = (entry.game && entry.game.detailsModalTextColor);
        const environmentTextDefined =
          (this.activeEnvironment && this.activeEnvironment.hubDetailsModalTextColor);
        if (override && gameTextDefined) {
          document.documentElement.style.setProperty(
            '--modalTextColor', entry.game.detailsModalTextColor
          );
        } else if (environmentTextDefined) {
          document.documentElement.style.setProperty(
            '--modalTextColor', this.activeEnvironment.hubDetailsModalTextColor
          );
        } else { // fallback values
          document.documentElement.style.setProperty(
            '--modalTextColor', entry.game.detailsModalTextColor || 'white'
          );
        }
      }
    },
    playStart() {
      const audio = new Audio(startSound);
      audio.play();
    },
    playRestart() {
      const audio = new Audio(restartSound);
      audio.play();
    },
    async onClassSelect(entry) {
      const id = entry._id;
      const gid = entry.game._id;
      if ((entry.inProgress && entry.complete) || entry.childGame) {
        await this.onClickPlay({ gameId: gid, classId: id, requestCompleted: true });
      } else {
        await this.onClickPlay({ gameId: gid, classId: id, inProgress: entry.inProgress });
      }
    },
    async onClassReset(entry) {
      const id = entry._id;
      const gid = entry.game._id;
      if ((entry.inProgress && entry.complete) || entry.childGame) {
        await this.onClickReset({ gameId: gid, classId: id, requestCompleted: true });
      } else {
        await this.onClickReset({ gameId: gid, classId: id });
      }
    },
    async onGameSelect(entry) {
      const id = entry.game._id;
      if ((entry.inProgress && entry.complete) || entry.childGame) {
        await this.onClickPlay({ gameId: id, requestCompleted: true });
      } else {
        await this.onClickPlay({ gameId: id, inProgress: entry.inProgress });
      }
    },
    async onGameReset(entry) {
      const id = entry.game._id;
      if (entry.complete && !entry.inProgress) {
        await this.onClickPlay({ gameId: id });
      } else {
        await this.onClickReset({ gameId: id });
      }
      // NOTE: can restart child game instead of parent, but this is never the expected behavior atm
      // if ((entry.inProgress && entry.complete) || entry.childGame) {
      //   await this.onClickReset({ gameId: id, requestCompleted: true });
      // } else {
      //   await this.onClickReset({ gameId: id });
      // }
    },
    async onClickPlay(groupSelector) {
      console.log(groupSelector);
      this.playStart();
      try {
        groupSelector.path = this.$route.path;

        // If we're requesting a completed game, we need to do the full getMyGroup so
        // it does the required post-processing.  Otherwise, if we know this is an
        // in-progress game, we can just get the groupId and skip the overhead of the
        // full request.
        let groupId =
          (!groupSelector.requestCompleted && groupSelector.inProgress)
            ? await this.getMyGroupIdOnly(groupSelector)
            : undefined;

        if (!groupId) {
          await this.getMyGroup(groupSelector);
          const group = this.group;
          if (group) {
            groupId = group._id;
          } else {
            const message = 'Failed to find or create group';
            this.flashDanger({ message });
            return;
          }
        }

        if (groupId) {
          this.$router.push(`/groups/${groupId}`);
          if (!this.isLti) { this.$router.go(0); }
        } else {
          const message = 'Failed to start';
          this.flashDanger({ message });
        }
      } catch (err) {
        const message = 'Failed to start';
        this.flashDanger({ message });
        console.error(err);
      }
    },
    async onClickReset(groupSelector) {
      this.playRestart();
      try {
        await this.archiveMyGroup(groupSelector);

        // Don't bother with the id request since we know we need to make
        // a new group.
        if (groupSelector.inProgress) delete groupSelector.inProgress;
        await this.onClickPlay(groupSelector);
      } catch (err) {
        const message = 'Failed to reset';
        this.flashDanger({ message });
        console.error(err);
      }
    },
    async init() {
      this.isLoadingComplete = false;
      try {
        this.classList = await this.getMyClasses({
          getAll: this.viewAll,
        });
        this.gameList = await this.getActiveGameSetGames({
          getAll: this.viewAll,
          shouldRefresh: true,
        });
        this.classList.sort(this.sortGameEntry);
        this.gameList.sort(this.sortClassEntry);
        await this.getActiveSignInConfig();
        await this.getActiveEnvironment({ path: this.$route.path });
        console.log({ gameList: this.gameList, classList: this.classList });
        if (!(await this.getShouldDefaultToHub())) {
          this.$router.replace('/demo');
        }
      } finally {
        await this.$nextTick();
        this.isLoadingComplete = true;
      }
    },
    sortGameEntry(a, b) {
      return this.sortEntry(a, b,
        (x, y) =>
          ((x.game || {}).thumbnailTitle || (x.game || {}).title || '')
            .localeCompare(((y.game || {}).thumbnailTitle || (y.game || {}).title || ''), undefined, { numeric: true }));
    },
    sortClassEntry(a, b) {
      return this.sortEntry(a, b,
        (x, y) =>
          (x.thumbnailTitle || x.courseName || x.className || x.game.thumbnailTitle || x.game.title || '')
            .localeCompare(
              (y.thumbnailTitle || y.courseName || y.className || y.game.thumbnailTitle || y.game.title || ''),
              undefined,
              { numeric: true }
            )
      );
    },
    sortEntry(a, b, fallback) {
      /* First, disabled goes last,
       * Second, !inProgress goes last.
       * Third, sort by the updated timestamp (generally for playable inProgress games).
       * (If it gets beyond this point, most likely both games have never been inProgress)
       * Fourth, sort by gameSetOrder,
       * And the rest shouldn't ever get hit by games now that gameSetOrder has been added,
       * but it could be hit by classes (or directly assigned games or when View All is used):
       * Fifth, completed goes first (but this is most likely not doing anything).
       * Sixth, if it has a childGame, it goes first.
       * Finally, if all that was equal, sort by fallback (generally a natural sort on title).
       */
      if ((a.disabled && b.disabled) || (!(a.disabled) && !(b.disabled))) {
        if ((a.inProgress && b.inProgress) || (!(a.inProgress) && !(b.inProgress))) {
          if ((!(a.updated) && !(b.updated)) || (a.updated === b.updated)) {
            if (((a.gameSetOrder == null) && (b.gameSetOrder == null))
              || (a.gameSetOrder === b.gameSetOrder)) {
              if ((a.complete && b.complete) || (!(a.complete) && !(b.complete))) {
                if ((a.childGame && b.childGame) || (!(a.childGame) && !(b.childGame))) {
                  return fallback ? fallback(a, b) : 0;
                } else if (a.childGame) {
                  return -1;
                }
                return 1;
              } else if (a.complete) {
                return -1;
              }
              return 1;
            } else if ((b.gameSetOrder == null)
              || (a.gameSetOrder != null && b.gameSetOrder != null
                && a.gameSetOrder < b.gameSetOrder)) {
              return -1;
            }
            return 1;
          } else if (!(b.updated)
            || (a.updated && b.updated
              && ((new Date(a.updated)) > (new Date(b.updated))))) {
            return -1;
          }
          return 1;
        } else if (a.inProgress) {
          return -1;
        }
        return 1;
      } else if (a.disabled) {
        return 1;
      }
      return -1;
    },
    async onChangeViewAll() {
      this.init();
    },
  },
  async created() {
    this.init();
  },
};
</script>

<style lang="scss" scoped>
.root {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  background-image: var(--titleBackground);
  background-size: cover;
  background-repeat: no-repeat;
  z-index: 10;
}

.game-hub-container {
  width: 100%;
  overflow-y: auto;
  scrollbar-color: var(--buttonColor) rgba(17, 17, 17, 0.5);
  scrollbar-width: thin;
}

.game-hub-container::-webkit-scrollbar {
  width: 14px;
}

.game-hub-container::-webkit-scrollbar-track {
  background: rgba(17, 17, 17, 0.5);
  border-radius: 10px;
}

.game-hub-container::-webkit-scrollbar-thumb {
  background: var(--buttonColor);
  border-radius: 10px;
}

.hub-header {
  color: var(--hubHeaderTextColor);
  width: 100%;
  padding: 10px 20px;
  margin-bottom: 5px;
  background-color: var(--hubHeaderBackgroundColor);
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
}

.hub-footer {
  display: flex;
  justify-content: space-evenly;
  align-items: center;
  padding-left: 10px;
  padding-right: 10px;
  padding-top: 5px;
  padding-bottom: 5px;
  width: 100%;
  height: 50px;
  // background-color: red;
  background-color: rgba(0, 0, 0, 0.6);
}

/deep/ .details-modal-base {
  color: var(--modalTextColor);
  background-color: transparent;
}

@media screen and (min-width: 320px) and (max-width: 767px) and (orientation: portrait) {
  /deep/ .details-modal-base {
    transform: rotate(-90deg);
    transform-origin: left top;
    width: calc(var(--detected-viewport-height) / 1.4);
    height: 100vw;
    overflow-x: visible;
    position: absolute;
    top: calc(var(--detected-viewport-height) / 1.1);
  }
}

/deep/ .details-modal-header-container {
  background-color: var(--modalBackgroundColor);
  border: 1px solid white;
  border-radius: 4px 4px 0 0;
}

.details-modal-header {
  width: 100%;
  height: 100%;
  background-color: transparent;
  border: none;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
}

.details-modal-close-button {
  margin-left: -1rem; // overwrites auto
  position: absolute;
  right: 1rem;
  color: var(--modalTextColor);
  // it's really silly that text-shadow doesn't have an intensity, instead stacking...
  text-shadow: 0 0 1px white, 0 0 1px white, 0 0 1px white, 0 0 1px white,
               0 0 1px white, 0 0 1px white, 0 0 1px white, 0 0 1px white,
               0 0 1px white, 0 0 1px white, 0 0 1px white, 0 0 1px white,
               0 0 1px white, 0 0 1px white, 0 0 1px white, 0 0 1px white;
}

.details-modal-body {
  background-color: var(--modalBackgroundColor);
  border: 1px solid white;
  border-radius: 0 0 4px 4px;
  width: 100%;
  height: 300px;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
}

.details-modal-image {
  width: 51%;
  margin: 2%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position-x: center;
  background-position-y: center;
  height: 96%;
}

.details-modal-content {
  height: 94%;
  width: 43%;
  margin: 3% 1%;
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  align-items: center;
  flex-grow: 1;
}

.details-modal-logo {
  width: 100%;
  height: 20%;
  margin-bottom: 7px;
  background-size: contain;
  background-repeat: no-repeat;
  background-position-x: center;
  background-position-y: center;
  max-height: 180px;
}

.details-modal-description {
  width: 100%;
  margin-bottom: 3px;
  overflow-y: auto;
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: 100px;
  font-size: 110%;
  line-height: 130%;
  scrollbar-color: var(--buttonColor) rgba(17, 17, 17, 0.5);
  scrollbar-width: thin;
}

.details-modal-description::-webkit-scrollbar {
  width: 14px;
}

.details-modal-description::-webkit-scrollbar-track {
  background: rgba(17, 17, 17, 0.5);
  border-radius: 10px;
}

.details-modal-description::-webkit-scrollbar-thumb {
  background: var(--buttonColor);
  border-radius: 10px;
}

.details-modal-button-container {
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
}

.details-modal-button-container button {
  width: 95%;
  text-transform: uppercase;
  font-size: 115%;
}
</style>



// WEBPACK FOOTER //
// src/components/game/pages/GameHub.vue